import classNames from 'classnames';
import type { FC, PropsWithChildren } from 'react';
import React, { useContext } from 'react';

import { RicosContext, RicosNodeContext, RicosViewerContext } from './../../../../../ricos-context/index.js';
import { Node_Type } from './../../../../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { GIFData, ImageData, PluginContainerData } from './../../../../../ricos-schema/next-schema/index.js';
import { ORIGINAL_SHAPE_WIDTH } from './../../../../../plugin-shape/utils/consts.js';

import { getPaywallSeoClass, isPaywallSeo } from '../../utils/paywallSeo.js';

type Props = {
  nodeType: Node_Type;
  componentData: ImageData;
  nodeIndex: number;
  className: string;
};

const disableCustomWidthMapper = {
  [Node_Type.IMAGE]: (componentData: ImageData, isMobile: boolean) =>
    componentData?.image?.width &&
    isMobile &&
    (typeof componentData?.containerData?.width?.custom === 'number'
      ? componentData.containerData.width.custom
      : parseFloat(componentData?.containerData?.width?.custom || '0')) > 150,
};

const isMediumOrFullWidth = (containerData?: PluginContainerData) => {
  return (
    containerData?.width?.size &&
    (containerData.width.size === 'CONTENT' || containerData.width?.size === 'FULL_WIDTH')
  );
};

const originalSizeMapper = {
  [Node_Type.IMAGE]: (componentData: ImageData) =>
    isMediumOrFullWidth(componentData.containerData) ? '100%' : `${componentData?.image?.width}px`,
  [Node_Type.GIF]: (componentData: GIFData) =>
    isMediumOrFullWidth(componentData.containerData) ? '100%' : `${componentData.width}px`,
  [Node_Type.SHAPE]: () => `${ORIGINAL_SHAPE_WIDTH}px`,
};

export const ContainerElement: FC<PropsWithChildren<Props>> = ({
  nodeType,
  componentData,
  nodeIndex,
  className,
  children,
}) => {
  const { containerData } = componentData;
  const { seoMode } = useContext(RicosViewerContext);
  const { isMobile } = useContext(RicosContext);
  const { parentNode } = useContext(RicosNodeContext);

  const disableCustomWidth = disableCustomWidthMapper[
    nodeType as $TSFixMeKey<typeof disableCustomWidthMapper>
  ]?.(componentData, isMobile);

  const customWidth =
    containerData?.width?.custom && !disableCustomWidth
      ? `${
          isNaN(Number(containerData.width.custom))
            ? containerData.width.custom
            : parseInt(containerData.width.custom)
        }px`
      : undefined;

  const originalWidth =
    (containerData?.width?.size === 'ORIGINAL' || disableCustomWidth) &&
    originalSizeMapper[nodeType as $TSFixMeKey<typeof originalSizeMapper>]?.(componentData);

  const width = customWidth || originalWidth;

  const style =
    componentData && width
      ? {
          width: `min(100%, ${width})`,
        }
      : undefined;

  return (
    <div
      className={classNames(
        className,
        !parentNode &&
          isPaywallSeo(seoMode) &&
          getPaywallSeoClass(seoMode?.paywall || {}, nodeIndex)
      )}
      style={style}
    >
      {children}
    </div>
  );
};
